// Dashboard del directivo — pantalla 1 del rediseño 2026-07 (mockups D5).
//
// Estructura (mockup):
//   Filtros: preset Hoy/Esta semana/Este mes + ◀ ▶ + filtro de PLANTA (cliente,
//     sin refetch — /directivo/resumen viene desglosado por planta).
//   Fila de 4 KPIs con deltas vs período equivalente anterior (regla F3).
//   Gráfico central "Cumplimiento y producción": turno + granularidad adaptativa
//     (Hoy→Hora; Semana→Día/Hora del día; Mes→Día/Semana/Hora del día) con
//     leyendas-filtro. "Hora del día" = perfil horario (PROMEDIO por franja).
//   "Costo de los paros" (valor no producido, por causal madre) — sigue los
//     mismos filtros del gráfico central.
//   Cards por planta (badges turno/sin captura + chips de áreas) + Top 5 líneas
//     por pérdida (sin tiendas: no capturan paros).
//   Footer: capacidad libre · fuera de programa · tiendas (aviso SKUs sin
//     precio) · confiabilidad del dato. El KPI de OTs entra cuando se abra la
//     puerta de facturación al rol (gap #1).
//
// Pendiente: presets Corte Q y Rango libre, versión mobile fina, variante
// mono-planta (áreas como cards).

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  DirectivoResumen,
  DirectivoResumenDetalle,
  EstadoPlantaResumen,
  GranularidadDetalle,
  ResumenPlantaPeriodo,
} from 'schemas';
import { Card } from '../../components/ui/card';
import { KpiCard } from '../../components/ui/kpi-card';
import { semaforoCumplimiento } from '../../lib/semaforo';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { SkeletonDashboard } from '../../components/ui/skeleton';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useDirectivoResumen, useDirectivoResumenDetalle } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import {
  CumplimientoProduccionChart,
  type PuntoChart,
} from './components/CumplimientoProduccionChart';
import { CostoParosChart } from './components/CostoParosChart';
import { ParosEnCursoChip } from './components/ParosEnCursoChip';
import {
  BarraPeriodo,
  FMT_DIA,
  GRANULARIDADES,
  hoyIso,
  labelBucket,
  limpiar,
  parseUTC,
  usePeriodo,
  type Preset,
} from './lib/periodo';
import { copM, horasMin, miles, pctEntero } from './lib/formato';
import { hayPendiente, textoPendiente } from './lib/pendiente';

// ============================================================================
// Agregación (el filtro de planta vive en el cliente)
// ============================================================================

interface Totales {
  unidades: number;
  meta: number;
  programa: number;
  realPlanif: number;
  fueraPlan: number;
  valor: number;
  costoMo: number;
  tiendasValor: number;
  tiendasInsumos: number;
  tiendasCostoMo: number;
  parosMin: number;
  parosEventos: number;
  /** DN1: paros abiertos del período — aparte, no suman a parosMin/parosEventos. */
  parosEnCurso: number;
  parosValor: number;
  /** DN9: producción sin precio vigente, fuera del cálculo de utilidad. */
  sinTarifaPaq: number;
  sinTarifaSkus: number;
  sinTarifaCostoMo: number;
  /** Bloques con precio pero sin costo hora-persona: fuera de la utilidad, declarados. */
  sinCostoMoPaq: number;
  sinCostoMoValor: number;
  /** DN10: capturado sin validar (líneas + tiendas) — declarado, NO suma al dinero. */
  pendBloques: number;
  pendPaq: number;
  pendValor: number;
  pendUtilidad: number;
}

function totalizar(filas: ResumenPlantaPeriodo[]): Totales {
  const t: Totales = {
    unidades: 0,
    meta: 0,
    programa: 0,
    realPlanif: 0,
    fueraPlan: 0,
    valor: 0,
    costoMo: 0,
    tiendasValor: 0,
    tiendasInsumos: 0,
    tiendasCostoMo: 0,
    parosMin: 0,
    parosEventos: 0,
    parosEnCurso: 0,
    parosValor: 0,
    sinTarifaPaq: 0,
    sinTarifaSkus: 0,
    sinTarifaCostoMo: 0,
    sinCostoMoPaq: 0,
    sinCostoMoValor: 0,
    pendBloques: 0,
    pendPaq: 0,
    pendValor: 0,
    pendUtilidad: 0,
  };
  for (const f of filas) {
    for (const p of [f.pendiente_validacion_lineas, f.pendiente_validacion_tiendas]) {
      if (!p) continue;
      t.pendBloques += p.bloques;
      t.pendPaq += p.paquetes;
      t.pendValor += p.valor_producido;
      t.pendUtilidad += p.utilidad;
    }
    t.unidades += f.unidades;
    t.meta += f.meta_capacidad_unds;
    t.programa += f.programa_unds;
    t.realPlanif += f.real_planif_unds;
    t.fueraPlan += f.fuera_plan_unds;
    t.valor +=
      (f.valor_lineas_cop ?? 0) + (f.valor_tiendas_cop ?? 0) + (f.ingreso_insumos_tiendas_cop ?? 0);
    t.costoMo += (f.costo_mo_lineas_cop ?? 0) + (f.costo_mo_tiendas_cop ?? 0);
    t.tiendasValor += f.valor_tiendas_cop ?? 0;
    t.tiendasInsumos += f.ingreso_insumos_tiendas_cop ?? 0;
    t.tiendasCostoMo += f.costo_mo_tiendas_cop ?? 0;
    t.parosMin += f.paros_min;
    t.parosEventos += f.paros_eventos;
    t.parosEnCurso += f.paros_en_curso;
    t.parosValor += f.paros_valor_cop ?? 0;
    t.sinTarifaPaq += (f.sin_tarifa_lineas_paq ?? 0) + (f.sin_tarifa_tiendas_paq ?? 0);
    t.sinTarifaSkus += (f.sin_tarifa_lineas_skus ?? 0) + (f.sin_tarifa_tiendas_skus ?? 0);
    t.sinTarifaCostoMo +=
      (f.sin_tarifa_lineas_costo_mo_cop ?? 0) + (f.sin_tarifa_tiendas_costo_mo_cop ?? 0);
    t.sinCostoMoPaq += (f.sin_costo_mo_lineas_paq ?? 0) + (f.sin_costo_mo_tiendas_paq ?? 0);
    t.sinCostoMoValor +=
      (f.sin_costo_mo_lineas_valor_cop ?? 0) + (f.sin_costo_mo_tiendas_valor_cop ?? 0);
  }
  return t;
}

// ============================================================================
// DN10: lo capturado sin validar se declara junto al KPI de dinero y NO suma.
// ============================================================================

function PendienteValidarLinea({
  bloques,
  paq,
  monto,
  etiqueta,
}: {
  /** Unidad de validación: un bloque de 0 paquetes puede costar mano de obra. */
  bloques: number;
  paq: number;
  monto: number;
  /** Qué es el monto ("de utilidad"); sin etiqueta es valor producido. */
  etiqueta?: string;
}) {
  const hay = hayPendiente({ bloques });
  return (
    <p
      className={cn('mt-2 px-5 text-[11px]', hay ? 'text-warn' : 'text-fg-tertiary')}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {textoPendiente({ bloques, paquetes: paq }, copM(monto), etiqueta)}
    </p>
  );
}

/** '2024-01-15' → '15 ene 2024' (es-CO). */
function fechaCorta(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ============================================================================
// Deltas
// ============================================================================

function variacionPct(actual: number, anterior: number): number | null {
  if (anterior <= 0) return null;
  return Math.round(((actual - anterior) / anterior) * 100);
}

function trendDe(
  delta: number | null,
  texto: (d: number) => string,
  tonoSube: 'ok' | 'crit',
  deltaLabel: string,
):
  | { direction: 'up' | 'down' | 'flat'; text: string; tone: 'ok' | 'warn' | 'crit' | 'neutral' }
  | undefined {
  if (delta === null) return undefined;
  if (delta === 0) return { direction: 'flat', text: `sin cambio ${deltaLabel}`, tone: 'neutral' };
  const sube = delta > 0;
  return {
    direction: sube ? 'up' : 'down',
    text: `${texto(delta)} ${deltaLabel}`,
    tone: sube ? tonoSube : tonoSube === 'ok' ? 'crit' : 'ok',
  };
}

// ============================================================================
// Página
// ============================================================================

type TurnoSel = 'todos' | '1' | '2' | '3';

export function DashboardPage() {
  const [plantaSel, setPlantaSel] = useState<string>('todas');
  const [granularidad, setGranularidad] = useState<GranularidadDetalle>('hora');
  const [turnoSel, setTurnoSel] = useState<TurnoSel>('todos');
  // Período en la URL (?p=&ancla= | ?p=rango&desde=&hasta=): refrescar / atrás
  // conservan el contexto, igual que en el coordinador (política 2.2).
  const ctrl = usePeriodo((p) => setGranularidad(GRANULARIDADES[p][0]!.value), 'hoy', {
    url: true,
  });
  const { preset, anchor, setAnchor, periodo } = ctrl;
  const turno = turnoSel === 'todos' ? null : Number(turnoSel);

  const resumen = useDirectivoResumen({
    desde: periodo.desde,
    hasta: periodo.hasta,
    desdeAnt: periodo.desdeAnt,
    hastaAnt: periodo.hastaAnt,
  });
  const detalle = useDirectivoResumenDetalle({
    desde: periodo.desde,
    hasta: periodo.hasta,
    granularidad,
    turno,
  });

  // "Hoy" es el día OPERATIVO del negocio (madrugada de turno que cruza →
  // ayer). El backend lo calcula; si difiere del día calendario, re-anclamos.
  const fechaOp = resumen.data?.fecha_operativa_hoy;
  useEffect(() => {
    if (preset === 'hoy' && fechaOp && anchor === hoyIso() && fechaOp !== anchor) {
      setAnchor(fechaOp);
    }
  }, [preset, fechaOp, anchor]);

  const headerExtra =
    preset === 'hoy' && resumen.data ? (
      <span className="hidden font-mono text-[11px] tracking-[0.04em] text-fg-tertiary lg:inline">
        Día operativo {limpiar(FMT_DIA.format(parseUTC(periodo.desde)))}
        {resumen.data.corte_hhmm ? ` · corte ${resumen.data.corte_hhmm}` : ''}
      </span>
    ) : undefined;

  return (
    <DirectivoLayout headerExtra={headerExtra}>
      {/* ── Barra de filtros ── */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <BarraPeriodo ctrl={ctrl} />
        <div className="flex-1" />
        {resumen.data && resumen.data.plantas.length > 1 ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Planta
            </span>
            <SegmentedControl
              value={plantaSel}
              onChange={setPlantaSel}
              size="sm"
              options={[
                { value: 'todas', label: `Todas (${resumen.data.plantas.length})` },
                ...resumen.data.plantas.map((p) => ({ value: p.planta_id, label: p.nombre })),
              ]}
            />
          </div>
        ) : null}
      </div>

      {resumen.isLoading ? (
        <SkeletonDashboard filas={6} etiqueta="Cargando resumen" />
      ) : resumen.isError || !resumen.data ? (
        <Card className="p-6 text-sm text-fg-secondary">
          No se pudo cargar el resumen. Intente de nuevo en unos segundos.
        </Card>
      ) : (
        <DashboardContenido
          data={resumen.data}
          detalle={detalle.data}
          detalleCargando={detalle.isLoading}
          plantaSel={plantaSel}
          deltaLabel={periodo.deltaLabel}
          periodoLabel={periodo.label}
          preset={preset}
          granularidad={granularidad}
          granularidades={GRANULARIDADES[preset]}
          onGranularidad={setGranularidad}
          turnoSel={turnoSel}
          onTurno={setTurnoSel}
          diasRango={
            (parseUTC(periodo.hasta).getTime() - parseUTC(periodo.desde).getTime()) / 86_400_000 + 1
          }
        />
      )}
    </DirectivoLayout>
  );
}

// ============================================================================
// Contenido (todo derivado de resumen + detalle, filtrado por planta)
// ============================================================================

function DashboardContenido({
  data,
  detalle,
  detalleCargando,
  plantaSel,
  deltaLabel,
  periodoLabel,
  preset,
  granularidad,
  granularidades,
  onGranularidad,
  turnoSel,
  onTurno,
  diasRango,
}: {
  data: DirectivoResumen;
  detalle: DirectivoResumenDetalle | undefined;
  detalleCargando: boolean;
  plantaSel: string;
  deltaLabel: string;
  periodoLabel: string;
  preset: Preset;
  granularidad: GranularidadDetalle;
  granularidades: Array<{ value: GranularidadDetalle; label: string }>;
  onGranularidad: (g: GranularidadDetalle) => void;
  turnoSel: TurnoSel;
  onTurno: (t: TurnoSel) => void;
  diasRango: number;
}) {
  const filtrarPlanta = <T extends { planta_id: string }>(filas: T[]): T[] =>
    plantaSel === 'todas' ? filas : filas.filter((f) => f.planta_id === plantaSel);

  const t = totalizar(filtrarPlanta(data.plantas));
  const ant = data.plantas_anterior ? totalizar(filtrarPlanta(data.plantas_anterior)) : null;
  const conCostos = data.puede_ver_costos;

  const utilidad = t.valor - t.costoMo;
  const margenPct = t.valor > 0 ? Math.round((utilidad / t.valor) * 100) : null;
  const cumplCliente = pctEntero(t.realPlanif, t.programa);
  const cumplCapacidad = pctEntero(t.unidades, t.meta);
  const antUtilidad = ant ? ant.valor - ant.costoMo : null;
  const antCumplCliente = ant ? pctEntero(ant.realPlanif, ant.programa) : null;

  // ── Serie del gráfico central ──
  // TODO el rendering del detalle se etiqueta con la granularidad que ECHÓ la
  // respuesta (con keepPreviousData el estado puede ir adelante de los datos).
  const granEfectiva: GranularidadDetalle = detalle?.granularidad ?? granularidad;

  const puntosChart: PuntoChart[] = useMemo(() => {
    if (!detalle) return [];
    const esPromedio = detalle.granularidad === 'hora_dia';
    // Nivel de servicio REAL por bucket (filtrado por planta, tope 100%).
    const realPorBucket = new Map<string, { prog: number; real: number; noEnt: number }>();
    for (const nr of filtrarPlanta(detalle.nivel_real ?? [])) {
      const acc = realPorBucket.get(nr.bucket) ?? { prog: 0, real: 0, noEnt: 0 };
      acc.prog += nr.programadas;
      acc.real += nr.realizadas;
      acc.noEnt += nr.no_entregadas;
      realPorBucket.set(nr.bucket, acc);
    }
    const porBucket = new Map<
      string,
      { produccion: number; meta: number; programa: number; realPlanif: number; dias: number }
    >();
    for (const p of filtrarPlanta(detalle.serie)) {
      const acc = porBucket.get(p.bucket) ?? {
        produccion: 0,
        meta: 0,
        programa: 0,
        realPlanif: 0,
        dias: 0,
      };
      acc.produccion += p.produccion;
      acc.meta += p.meta_capacidad_unds;
      acc.programa += p.programa_unds ?? 0;
      acc.realPlanif += p.real_planif_unds ?? 0;
      acc.dias = Math.max(acc.dias, p.dias_con_dato);
      porBucket.set(p.bucket, acc);
    }
    const conPrograma = detalle.serie.some((p) => p.programa_unds !== null);
    return [...porBucket.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, acc]) => {
        const div = esPromedio && acc.dias > 0 ? acc.dias : 1;
        const clientePct =
          conPrograma && acc.programa > 0
            ? Math.round((acc.realPlanif / acc.programa) * 100)
            : null;
        const capacidadPct = acc.meta > 0 ? Math.round((acc.produccion / acc.meta) * 100) : null;
        // Anti-choque de etiquetas: si las dos líneas quedan a <10 pp en el
        // mismo bucket, solo se etiqueta el valor MÁS ALTO (pedido 2026-07-04).
        const chocan =
          clientePct !== null && capacidadPct !== null && Math.abs(clientePct - capacidadPct) < 10;
        const nr = realPorBucket.get(bucket);
        const realPct =
          nr && nr.prog > 0
            ? Math.min(100, Math.round(((nr.real + nr.noEnt) / nr.prog) * 100))
            : null;
        return {
          bucket,
          label: labelBucket(detalle.granularidad, bucket),
          produccion: Math.round(acc.produccion / div),
          meta: Math.round(acc.meta / div),
          programa: conPrograma ? Math.round(acc.programa / div) : null,
          cliente_pct: clientePct,
          capacidad_pct: capacidadPct,
          real_pct: realPct,
          cliente_label: chocan && clientePct! < capacidadPct! ? null : clientePct,
          capacidad_label: chocan && capacidadPct! <= clientePct! ? null : capacidadPct,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle, plantaSel]);

  const conPrograma = (detalle?.serie ?? []).some((p) => p.programa_unds !== null);

  // ── Top 5 líneas por pérdida (tras filtro de planta) ──
  const top5 = filtrarPlanta(detalle?.perdida_lineas ?? [])
    .slice()
    .sort((a, b) => b.unds_perdidas - a.unds_perdidas)
    .slice(0, 5);
  const maxPerdida = top5[0]?.unds_perdidas ?? 0;

  // ── Footer ──
  const capacidadLibrePct = t.meta > 0 ? Math.max(0, 100 - (cumplCapacidad ?? 0)) : null;
  const paqDisponiblesDia = t.meta > 0 ? Math.max(0, t.meta - t.unidades) / diasRango : null;
  const fueraPlanPctTotal = pctEntero(t.fueraPlan, t.unidades);
  const estadoFiltrado = filtrarPlanta(data.estado_plantas);
  const capturasTotal = estadoFiltrado.reduce((s, e) => s + e.capturas_total, 0);
  const capturasValidadas = estadoFiltrado.reduce((s, e) => s + e.capturas_validadas, 0);
  const skusSinPrecio = estadoFiltrado.reduce((s, e) => s + e.tiendas_skus_sin_precio, 0);
  // DN10: bloques sin validar del período y arrastre histórico (sin período)
  // de la planta seleccionada (o de todas).
  const pendBloquesPeriodo = capturasTotal - capturasValidadas;
  const pendPctPeriodo = pctEntero(pendBloquesPeriodo, capturasTotal);
  const histFiltrado = filtrarPlanta(data.pendiente_historico?.por_planta ?? []);
  const histBloques = histFiltrado.reduce((s, h) => s + h.bloques, 0);
  const histPaq = histFiltrado.reduce((s, h) => s + h.paquetes, 0);
  const histDesde = histFiltrado.reduce<string | null>(
    (m, h) => (h.desde !== null && (m === null || h.desde < m) ? h.desde : m),
    null,
  );

  const estadoMap = new Map(data.estado_plantas.map((e) => [e.planta_id, e]));

  // Resumen de filtros activos — se repite en los bloques que los heredan
  // para que sea evidente que el período/planta/turno de arriba filtran ahí.
  const plantaNombre =
    plantaSel === 'todas'
      ? `Todas las plantas`
      : (data.plantas.find((p) => p.planta_id === plantaSel)?.nombre ?? '');
  const filtrosLabel = `${periodoLabel} · ${plantaNombre} · ${
    turnoSel === 'todos' ? 'Todos los turnos' : `Turno ${turnoSel}`
  }`;

  return (
    <>
      {/* ── Fila de KPIs ── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {conCostos ? (
          <Card className="py-4">
            <KpiCard
              label="Valor producido · COP"
              hint={`Base: producción validada por supervisión, la misma del informe gerencial del coordinador. Lo capturado que el supervisor aún no valida se declara debajo como pendiente y no suma. Líneas ${copM(t.valor - t.tiendasValor - t.tiendasInsumos)} + tiendas ${copM(t.tiendasValor)} + insumos cobrados ${copM(t.tiendasInsumos)}. Misma fórmula que el coordinador (precio vigente por SKU, solo producto con tarifa).`}
              value={copM(t.valor)}
              sub={`validado · líneas + tiendas · ${miles(t.unidades)} paq capturados`}
              trend={trendDe(
                ant ? variacionPct(t.valor, ant.valor) : null,
                (d) => `${d > 0 ? '+' : ''}${d}%`,
                'ok',
                deltaLabel,
              )}
            />
            <PendienteValidarLinea bloques={t.pendBloques} paq={t.pendPaq} monto={t.pendValor} />
          </Card>
        ) : (
          <Card className="py-4">
            <KpiCard
              label="Producción · paquetes"
              value={miles(t.unidades)}
              sub="unidades válidas del período"
              trend={trendDe(
                ant ? variacionPct(t.unidades, ant.unidades) : null,
                (d) => `${d > 0 ? '+' : ''}${d}%`,
                'ok',
                deltaLabel,
              )}
            />
          </Card>
        )}

        {conCostos ? (
          <Card className="py-4">
            <KpiCard
              label="Utilidad estimada · MO"
              hint={`Utilidad = valor producido − mano de obra (operarios × horas reales × costo hora-persona), sobre producción validada por supervisión y solo producto con precio y costo vigentes. ${
                t.sinTarifaPaq > 0
                  ? `Sin precio: ${miles(t.sinTarifaPaq)} paq de ${t.sinTarifaSkus} SKU(s), con ${copM(t.sinTarifaCostoMo)} de mano de obra sin asignar — cargue los precios en Configuración → SKUs. `
                  : ''
              }${
                t.sinCostoMoPaq > 0
                  ? `Sin costo hora-persona: ${miles(t.sinCostoMoPaq)} paq por ${copM(t.sinCostoMoValor)} de valor — registre el costo de mano de obra de la planta. `
                  : ''
              }${t.sinTarifaPaq === 0 && t.sinCostoMoPaq === 0 ? 'Toda la producción del período tiene precio y costo vigentes.' : 'Nada de eso se muestra como pérdida ni como utilidad.'}`}
              value={copM(utilidad)}
              valueSuffix={margenPct !== null ? `${margenPct}%` : undefined}
              sub={
                t.sinTarifaPaq + t.sinCostoMoPaq > 0
                  ? `Ingresos ${copM(t.valor)} — MO ${copM(t.costoMo)} · ${miles(t.sinTarifaPaq + t.sinCostoMoPaq)} paq fuera del cálculo (ver ⓘ)`
                  : `Ingresos ${copM(t.valor)} — Costo MO ${copM(t.costoMo)}`
              }
              trend={trendDe(
                antUtilidad !== null ? variacionPct(utilidad, antUtilidad) : null,
                (d) => `${d > 0 ? '+' : ''}${d}% utilidad`,
                'ok',
                deltaLabel,
              )}
            />
            <PendienteValidarLinea
              bloques={t.pendBloques}
              paq={t.pendPaq}
              monto={t.pendUtilidad}
              etiqueta="de utilidad"
            />
          </Card>
        ) : (
          <Card className="py-4">
            <KpiCard
              label="Capacidad utilizada"
              value={cumplCapacidad !== null ? `${cumplCapacidad}` : '—'}
              valueSuffix={cumplCapacidad !== null ? '%' : undefined}
              sub={`Meta del período: ${miles(t.meta)} paq`}
            />
          </Card>
        )}

        <Card className="py-4">
          <KpiCard
            label="Cumplimiento del período"
            hint={`Programado del período: ${miles(t.programa)} paq. Cliente = real de lo programado / programa. Capacidad = producción / meta de capacidad instalada.`}
            value={
              // Captions EN LÍNEA con los números (misma altura de bloque que
              // las otras cards — apilarlas debajo desalineaba toda la fila).
              <span className="whitespace-nowrap tabular-nums">
                {cumplCliente !== null ? `${cumplCliente}%` : 'n/s'}
                <span className="ml-1.5 mr-3 text-[10px] font-medium uppercase tracking-[0.07em] text-fg-tertiary">
                  Cliente
                </span>
                {cumplCapacidad !== null ? `${cumplCapacidad}%` : 'n/s'}
                <span className="ml-1.5 text-[10px] font-medium uppercase tracking-[0.07em] text-fg-tertiary">
                  Capacidad
                </span>
              </span>
            }
            trend={
              cumplCliente !== null && antCumplCliente !== null
                ? trendDe(
                    cumplCliente - antCumplCliente,
                    (d) => `${d > 0 ? '+' : ''}${d} pp cliente`,
                    'ok',
                    deltaLabel,
                  )
                : undefined
            }
            sub={`Programado: ${miles(t.programa)} paq`}
          />
        </Card>

        <Card className="py-4">
          <KpiCard
            label={conCostos ? 'Paros · valor no producido' : 'Paros'}
            value={conCostos ? copM(t.parosValor) : horasMin(t.parosMin)}
            sub={
              <span className="inline-flex flex-wrap items-center gap-1.5">
                {conCostos
                  ? `${horasMin(t.parosMin)} · ${t.parosEventos} eventos`
                  : `${t.parosEventos} eventos`}
                <ParosEnCursoChip cantidad={t.parosEnCurso} />
              </span>
            }
            trend={trendDe(
              ant
                ? conCostos
                  ? variacionPct(t.parosValor, ant.parosValor)
                  : variacionPct(t.parosMin, ant.parosMin)
                : null,
              (d) => `${d > 0 ? '+' : ''}${d}%`,
              'crit',
              deltaLabel,
            )}
          />
        </Card>
      </div>

      {/* ── Cuerpo en 2 filas alineadas: [gráfico | plantas] y [paros | top 5]
           (las cards de planta estiran para cubrir la altura del gráfico) ── */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Card className="p-5 xl:col-span-2">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h2 className="text-sm font-medium text-fg">Cumplimiento y producción</h2>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Turno
              </span>
              <SegmentedControl
                value={turnoSel}
                onChange={onTurno}
                size="sm"
                options={[
                  { value: 'todos', label: 'Todos' },
                  { value: '1', label: 'T1' },
                  { value: '2', label: 'T2' },
                  { value: '3', label: 'T3' },
                ]}
              />
            </div>
            {granularidades.length > 1 ? (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  Granularidad
                </span>
                <SegmentedControl
                  value={granularidad}
                  onChange={onGranularidad}
                  size="sm"
                  options={granularidades}
                />
              </div>
            ) : null}
          </div>
          {granularidad === 'hora_dia' ? (
            <p className="mb-2 text-[11px] text-fg-tertiary">
              Perfil horario: promedio por franja sobre los días del período — muestra en qué horas
              se produce más.
            </p>
          ) : null}
          {detalleCargando && !detalle ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <CumplimientoProduccionChart data={puntosChart} conPrograma={conPrograma} />
          )}
        </Card>

        {/* Columna derecha fila 1: las plantas cubren la altura del gráfico */}
        <div className="flex flex-col gap-5">
          {filtrarPlanta(data.plantas).map((p) => (
            <PlantaCard
              key={p.planta_id}
              fila={p}
              estado={estadoMap.get(p.planta_id)}
              className="flex-1"
              detallado={plantaSel !== 'todas'}
            />
          ))}
        </div>

        <Card className="p-5 xl:col-span-2">
          <div className="mb-1 flex flex-wrap items-baseline gap-3">
            <h2 className="text-sm font-medium text-fg">Costo de los paros</h2>
            <span className="text-[11px] text-fg-tertiary">
              valor no producido a precio vigente
            </span>
          </div>
          <p className="mb-3 text-[11px] font-medium text-fg-secondary">Filtrado: {filtrosLabel}</p>
          <CostoParosChart
            puntos={filtrarPlanta(detalle?.paros_causal ?? [])}
            labelDe={(b) => labelBucket(granEfectiva, b)}
            conCostos={conCostos}
          />
        </Card>

        {/* Columna derecha fila 2: top 5 empareja con el costo de paros */}
        <Card className="p-5">
          <h2 className="text-sm font-medium text-fg">Top 5 líneas por pérdida</h2>
          <p className="mb-4 mt-0.5 text-[11px] font-medium text-fg-secondary">
            Filtrado: {filtrosLabel}
          </p>
          {top5.length === 0 ? (
            <p className="text-sm text-fg-tertiary">Sin pérdidas por paro en el período</p>
          ) : (
            <div className="flex flex-col gap-3">
              {top5.map((l) => (
                <div key={l.linea_id} className="flex items-center gap-3">
                  <span className="w-20 truncate font-mono text-[11px] uppercase text-fg-secondary">
                    {l.codigo}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-sm bg-surface-overlay">
                    <div
                      className="h-full rounded-sm bg-fg-tertiary"
                      style={{
                        width: `${maxPerdida > 0 ? Math.max(4, (l.unds_perdidas / maxPerdida) * 100) : 0}%`,
                      }}
                    />
                  </div>
                  <span className="whitespace-nowrap font-mono text-[11px] tabular-nums text-fg-secondary">
                    {miles(l.unds_perdidas)} paq
                    {conCostos && l.valor_cop !== null ? ` · ${copM(l.valor_cop)}` : ''}
                  </span>
                </div>
              ))}
              <p className="text-[10px] text-fg-tertiary">
                paq no producidos por paros · valor a precio vigente
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* ── Footer: métricas de cierre + confiabilidad ── */}
      <div className="mt-5 grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-3">
        <FooterStat
          label="Capacidad libre"
          value={capacidadLibrePct !== null ? `${capacidadLibrePct}%` : 'n/s'}
          sub={
            paqDisponiblesDia !== null
              ? `= ${miles(paqDisponiblesDia)} paq/día disponibles`
              : 'sin meta de capacidad en el período'
          }
        />
        <FooterStat
          label="Fuera de programa"
          value={`${miles(t.fueraPlan)} paq`}
          sub={
            fueraPlanPctTotal !== null
              ? `${fueraPlanPctTotal}% del total · extra + rework`
              : 'extra + rework'
          }
        />
        {conCostos ? (
          <FooterStat
            label="Costo de mano de obra"
            value={copM(t.costoMo)}
            valueTone={t.valor > 0 && t.costoMo > t.valor ? 'crit' : undefined}
            sub={
              t.valor > 0
                ? `${Math.round((t.costoMo / t.valor) * 100)}% del ingreso del período`
                : 'sin ingresos valorizados en el período'
            }
          />
        ) : (
          <FooterStat
            label="Paro total"
            value={horasMin(t.parosMin)}
            sub={
              <span className="inline-flex flex-wrap items-center gap-1.5">
                {`${t.parosEventos} eventos que afectaron disponibilidad`}
                <ParosEnCursoChip cantidad={t.parosEnCurso} />
              </span>
            }
          />
        )}
      </div>
      {/* DN10: lo que falta validar en el período y el arrastre histórico de la
          planta, visible (no letra pequeña): es lo que separa el dinero mostrado
          del capturado. */}
      <Card className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2.5 text-[12px]">
        <span
          className={cn(
            pendBloquesPeriodo > 0 || histBloques > 0 ? 'text-warn' : 'text-fg-secondary',
          )}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {capturasTotal > 0
            ? `Sin validar en el período: ${miles(pendBloquesPeriodo)} bloques (${pendPctPeriodo ?? 0} %)`
            : 'Sin capturas en el período'}
          {` · Acumulado de ${plantaSel === 'todas' ? 'las plantas' : 'la planta'}${
            histDesde ? ` desde ${fechaCorta(histDesde)}` : ''
          }: ${miles(histBloques)} bloques · ${miles(histPaq)} paq`}
          {conCostos && skusSinPrecio > 0
            ? ` · ⚠ ${skusSinPrecio} SKUs de tienda sin precio — valor parcial`
            : ''}
        </span>
        <Link to="/directivo/auditoria" className="text-fg-secondary underline hover:text-fg">
          Ver auditoría →
        </Link>
      </Card>
    </>
  );
}

// ============================================================================
// Card de planta (badges + stats + chips de áreas)
// ============================================================================

// DN5: semáforo de OPERACIÓN compartido (90/70), el mismo del coordinador.
// Antes 85/60: la misma área al 87 % era verde acá y ámbar en C1.
function chipTone(a: { unidades: number; meta_capacidad_unds: number; paro_activo: boolean }) {
  if (a.paro_activo) return 'bg-crit';
  if (a.meta_capacidad_unds <= 0) return 'bg-fg-tertiary';
  const tone = semaforoCumplimiento((a.unidades / a.meta_capacidad_unds) * 100);
  return tone === 'ok' ? 'bg-ok' : tone === 'warn' ? 'bg-warn' : 'bg-crit';
}

function PlantaCard({
  fila,
  estado,
  className,
  detallado = false,
}: {
  fila: ResumenPlantaPeriodo;
  estado: EstadoPlantaResumen | undefined;
  className?: string;
  /** Con una sola planta filtrada la card tiene espacio: desglose por área
   *  como mini-tabla (paq + % capacidad) en vez de la tira de chips. */
  detallado?: boolean;
}) {
  const cli = pctEntero(fila.real_planif_unds, fila.programa_unds);
  const cap = pctEntero(fila.unidades, fila.meta_capacidad_unds);
  const sinCaptura = estado?.sin_captura_desde_hhmm ?? null;
  const turnoActivo = estado?.turno_activo_numero ?? null;

  return (
    <Card className={cn('p-5', className)}>
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            'inline-block size-2 rounded-full',
            sinCaptura ? 'bg-crit' : turnoActivo ? 'bg-ok' : 'bg-fg-tertiary',
          )}
        />
        <p className="text-base font-semibold text-fg">{fila.nombre}</p>
        {sinCaptura ? (
          <span className="rounded border border-crit/40 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-crit">
            Sin captura{turnoActivo ? ` · T${turnoActivo}` : ''}
          </span>
        ) : turnoActivo ? (
          <span className="rounded border border-ok/40 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-ok">
            T{turnoActivo} activo
          </span>
        ) : null}
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2">
        <MiniStat label="paq" value={miles(fila.unidades)} />
        <MiniStat label="cliente" value={cli !== null ? `${cli}%` : 'n/s'} />
        <MiniStat label="capacidad" value={cap !== null ? `${cap}%` : 'n/s'} />
        <MiniStat label="paro" value={horasMin(fila.paros_min)} />
      </div>
      {estado && estado.areas.length > 0 ? (
        detallado ? (
          <div className="mt-3 border-t border-border pt-2.5">
            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Por área
            </p>
            <div className="flex flex-col gap-1.5">
              {[...estado.areas]
                .sort((a, b) => b.unidades - a.unidades)
                .map((a) => {
                  const pct =
                    a.meta_capacidad_unds > 0
                      ? Math.round((a.unidades / a.meta_capacidad_unds) * 100)
                      : null;
                  return (
                    <div key={a.area_id} className="flex items-center gap-2 text-[12px]">
                      <span className={cn('inline-block size-1.5 rounded-full', chipTone(a))} />
                      <span className="flex-1 truncate text-fg-secondary">{a.nombre}</span>
                      <span className="tabular-nums text-fg">{miles(a.unidades)} paq</span>
                      <span className="w-12 text-right tabular-nums text-fg-tertiary">
                        {pct !== null ? `${pct}%` : 's/m'}
                      </span>
                    </div>
                  );
                })}
            </div>
            <p className="mt-1.5 text-[10px] text-fg-tertiary">
              % vs capacidad instalada · s/m = sin meta
            </p>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 border-t border-border pt-2.5">
            {estado.areas.map((a) => (
              <span
                key={a.area_id}
                className="inline-flex items-center gap-1.5 text-[11px] text-fg-secondary"
                title={
                  a.meta_capacidad_unds > 0
                    ? `${a.nombre}: ${Math.round((a.unidades / a.meta_capacidad_unds) * 100)}% de capacidad`
                    : `${a.nombre}: sin meta de capacidad`
                }
              >
                <span className={cn('inline-block size-1.5 rounded-full', chipTone(a))} />
                {a.nombre}
              </span>
            ))}
          </div>
        )
      ) : null}
      {sinCaptura ? (
        <p className="mt-2.5 text-[11px] font-medium text-crit">Sin capturas desde {sinCaptura}</p>
      ) : null}
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm font-medium tabular-nums text-fg">{value}</p>
      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-fg-tertiary">
        {label}
      </p>
    </div>
  );
}

function FooterStat({
  label,
  value,
  sub,
  valueTone,
}: {
  label: string;
  value: string;
  sub: ReactNode;
  valueTone?: 'crit';
}) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">{label}</p>
      <p
        className={cn(
          'mt-1 text-lg font-medium tabular-nums',
          valueTone === 'crit' ? 'text-crit' : 'text-fg',
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[11px] text-fg-tertiary">{sub}</p>
    </div>
  );
}
